/**
 * ModalAlerta - Componente de Modal de Alerta/Confirmação
 * 
 * CONTEXTO DE NEGÓCIO:
 * Modal genérico e reutilizável para substituir o `alert()` nativo do navegador.
 * Fornece uma experiência de usuário consistente com a identidade visual do sistema.
 * 
 * FUNCIONALIDADES:
 * - Exibir mensagens de alerta simples (apenas OK)
 * - Exibir mensagens de confirmação (OK + Cancelar)
 * - Callbacks personalizáveis para ações dos botões
 * - Overlay semi-transparente para focar atenção no modal
 * - Animações suaves de entrada/saída
 * 
 * RESPONSABILIDADES:
 * - Renderizar modal com título e mensagem
 * - Gerenciar estado de visibilidade
 * - Executar callbacks ao confirmar ou cancelar
 * - Garantir acessibilidade (ARIA attributes)
 * - Bloquear interação com elementos de fundo
 * 
 * INTEGRAÇÃO:
 * Controlado pelo ModalContext, que gerencia o estado global do modal.
 */

import React, { useEffect } from 'react';

/**
 * Tipo de modal que determina quais botões serão exibidos
 * - 'alerta': Apenas botão "OK" (para mensagens informativas)
 * - 'confirmacao': Botões "OK" e "Cancelar" (para ações que requerem confirmação)
 */
export type TipoModalAlerta = 'alerta' | 'confirmacao';

/**
 * Propriedades do componente ModalAlerta
 */
export interface PropriedadesModalAlerta {
  /** Indica se o modal está visível */
  estaVisivel: boolean;
  
  /** Título do modal (linha de cabeçalho) */
  titulo: string;
  
  /** Mensagem principal do modal (corpo do texto) */
  mensagem: string;
  
  /** Tipo do modal: 'alerta' (só OK) ou 'confirmacao' (OK + Cancelar) */
  tipo: TipoModalAlerta;
  
  /** Callback executado quando o usuário clica em "OK" ou "Confirmar" */
  aoConfirmar: () => void;
  
  /** Callback executado quando o usuário clica em "Cancelar" (opcional, usado apenas em tipo 'confirmacao') */
  aoCancelar?: () => void;
  
  /** Texto customizado para o botão de confirmação (padrão: "OK" para alerta, "Confirmar" para confirmacao) */
  textoConfirmar?: string;
  
  /** Texto customizado para o botão de cancelamento (padrão: "Cancelar") */
  textoCancelar?: string;
}

/**
 * Componente de Modal de Alerta/Confirmação
 * 
 * IMPLEMENTAÇÃO:
 * 1. Renderiza um overlay semi-transparente que cobre toda a tela
 * 2. Centraliza o conteúdo do modal na tela
 * 3. Exibe título, mensagem e botões de ação
 * 4. Gerencia o foco e acessibilidade com atributos ARIA
 * 5. Permite fechamento ao clicar no overlay (apenas para alertas simples)
 * 6. Bloqueia scroll do body quando modal está ativo
 * 
 * ESTILIZAÇÃO:
 * - Overlay: fundo escuro semi-transparente (bg-black/50)
 * - Container: fundo branco, cantos arredondados, sombra
 * - Botões: estilizados conforme padrão do sistema (bg-blue-600 para primário)
 * - Animações: transições suaves de opacidade e escala
 * 
 * Args:
 *   estaVisivel: Controla a visibilidade do modal
 *   titulo: Texto do cabeçalho do modal
 *   mensagem: Texto do corpo da mensagem
 *   tipo: Define se é um alerta simples ou confirmação
 *   aoConfirmar: Função chamada ao confirmar
 *   aoCancelar: Função chamada ao cancelar (opcional)
 *   textoConfirmar: Customiza texto do botão de confirmação
 *   textoCancelar: Customiza texto do botão de cancelamento
 * 
 * Returns:
 *   JSX.Element: Modal renderizado ou null se não estiver visível
 */
export function ModalAlerta({
  estaVisivel,
  titulo,
  mensagem,
  tipo,
  aoConfirmar,
  aoCancelar,
  textoConfirmar,
  textoCancelar
}: PropriedadesModalAlerta): JSX.Element | null {
  
  // Efeito: Bloqueia scroll do body quando modal está visível
  // WHY: Prevenir que o usuário role a página enquanto o modal está ativo,
  // garantindo que a atenção permaneça no modal
  useEffect(() => {
    if (estaVisivel) {
      // Salva o valor atual de overflow para restaurar depois
      const overflowAnterior = document.body.style.overflow;
      document.body.style.overflow = 'hidden';
      
      // Cleanup: Restaura o overflow quando o modal é fechado
      return () => {
        document.body.style.overflow = overflowAnterior;
      };
    }
  }, [estaVisivel]);
  
  // Se o modal não está visível, não renderiza nada
  if (!estaVisivel) {
    return null;
  }
  
  /**
   * Handler para clique no overlay (fundo escuro)
   * WHY: Permite fechar o modal clicando fora dele (apenas para alertas simples)
   * Para confirmações, forçamos o usuário a clicar em um dos botões
   */
  const handleClickOverlay = (evento: React.MouseEvent<HTMLDivElement>) => {
    // Só fecha se clicar diretamente no overlay (não no conteúdo do modal)
    if (evento.target === evento.currentTarget && tipo === 'alerta') {
      aoConfirmar();
    }
  };
  
  /**
   * Handler para o botão de confirmação
   * WHY: Executa a ação de confirmação e fecha o modal
   */
  const handleConfirmar = () => {
    aoConfirmar();
  };
  
  /**
   * Handler para o botão de cancelamento
   * WHY: Executa a ação de cancelamento (se fornecida) e fecha o modal
   */
  const handleCancelar = () => {
    if (aoCancelar) {
      aoCancelar();
    }
  };
  
  // Define textos padrão dos botões baseado no tipo do modal
  const textoFinalConfirmar = textoConfirmar || (tipo === 'alerta' ? 'OK' : 'Confirmar');
  const textoFinalCancelar = textoCancelar || 'Cancelar';
  
  return (
    // Overlay: Fundo semi-transparente que cobre toda a tela
    // role="dialog": Indica para leitores de tela que é uma caixa de diálogo
    // aria-modal="true": Indica que é um modal (bloqueia interação com o resto da página)
    // aria-labelledby: Conecta com o título para acessibilidade
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm transition-opacity duration-300"
      onClick={handleClickOverlay}
      role="dialog"
      aria-modal="true"
      aria-labelledby="modal-titulo"
      aria-describedby="modal-mensagem"
    >
      {/* Container do Modal: Caixa branca centralizada com conteúdo */}
      <div
        className="mx-4 w-full max-w-md scale-100 rounded-xl bg-white shadow-2xl transition-all duration-300"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Cabeçalho do Modal: Título */}
        <div className="border-b border-gray-200 px-6 py-4">
          <h2
            id="modal-titulo"
            className="text-xl font-semibold text-gray-900"
          >
            {titulo}
          </h2>
        </div>
        
        {/* Corpo do Modal: Mensagem */}
        <div className="p-6">
          <p
            id="modal-mensagem"
            className="whitespace-pre-wrap text-base leading-relaxed text-gray-700"
          >
            {mensagem}
          </p>
        </div>
        
        {/* Rodapé do Modal: Botões de Ação */}
        <div className="flex justify-end space-x-3 rounded-b-xl bg-gray-50 px-6 py-4">
          {/* Botão Cancelar: Apenas exibido para modais de confirmação */}
          {tipo === 'confirmacao' && (
            <button
              onClick={handleCancelar}
              className="rounded-lg border border-gray-300 bg-white px-5 py-2.5 font-medium text-gray-700 transition-colors hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-gray-300"
              type="button"
            >
              {textoFinalCancelar}
            </button>
          )}
          
          {/* Botão Confirmar/OK: Sempre exibido */}
          <button
            onClick={handleConfirmar}
            className="rounded-lg bg-blue-600 px-5 py-2.5 font-medium text-white transition-colors hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-300"
            type="button"
            autoFocus
          >
            {textoFinalConfirmar}
          </button>
        </div>
      </div>
    </div>
  );
}
